import { ConfiguratorOrigin, ConfiguratorOriginsType } from '../constants';
import type {
  ConfigurationMonogramDetails,
  DefaultValues,
  ExistingOrderDetails,
  Fabric,
  Monogram,
  Option,
  OptionConfigurationType,
  OptionsSelection,
  OptionValue,
  SizePassportSelection,
  StylePreset,
  StylePresets,
  typeFabricList,
  typeOptionValue,
} from '../interfaces';
import { getSectionByConfigurationType, Section } from '../navigation';
import type { ConfigurationTypes, ProductType } from '../productDefinition';
import {
  getExtraConfgurationByBaseConfiguration,
  getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId,
  Products,
} from '../productDefinition';
import { initialiseConfigurationBasedOptionsSelection, isExtraConfigurationInProduct, isObjectEmpty } from '../utils';

export type MissingOptionValuesPerConfiguration = {
  [key: number]: MissingOptionValues;
};

export type MissingOptionsPerConfiguration = {
  [key: number]: MissingOptions;
};

export type MissingOptions = {
  [key: number]: Option;
};

export type MissingOptionValues = {
  [key: number]: OptionValue;
};

export type MissingMonogramValuesPerConfig = {
  [key: number]: Monogram;
};

export const getExistingConfigurationSelectedFabric = (
  savedConfiguration: ExistingOrderDetails,
  fabricList: typeFabricList,
) => {
  const selectedFabricId = savedConfiguration.subConfigurations[0].fabricId;
  return fabricList[selectedFabricId];
};

export const getExistingConfigurationSelectAndMissingOptionValues = (
  savedConfiguration: ExistingOrderDetails,
  options: OptionConfigurationType,
  optionValues: typeOptionValue,
  defaultValues: DefaultValues,
  product: ProductType,
  stylePresets: StylePresets,
  origin: ConfiguratorOriginsType,
) => {
  let optionsToPreselect: OptionsSelection = getNotAddedOptionalConfigurationsDefaultValues(
    savedConfiguration,
    defaultValues,
    product,
  );
  let monogramsToPreselect: Monogram = {};
  let stylePresetsToPreselect: {
    configurationType: ConfigurationTypes;
    baseStyle: StylePreset;
  }[] = [];
  let sizePassportsToPreselect: SizePassportSelection = {};
  let missingOptionValuesPerConfig: MissingOptionValuesPerConfiguration = {};
  let missingMonogramValuesPerConfig: MissingMonogramValuesPerConfig = {};

  savedConfiguration?.subConfigurations.forEach(subConfiguration => {
    const {
      optionValues: subConfigurationOptionValues,
      configurationTypeId: baseConfigurationId,
      monograms,
      stylePresetId,
      sizePassportId,
    } = subConfiguration;

    let configurationId = baseConfigurationId;

    // To handle extra configurations which have different configuration type id but using definitions from base product
    // Check the length of subConfigurations to know if it is a single or multiple configurations in a product
    if (subConfiguration.isExtraProduct && savedConfiguration?.subConfigurations.length > 1) {
      const extraConfigurationId = getExtraConfgurationByBaseConfiguration(
        savedConfiguration.product,
        subConfiguration.configurationTypeId.toString() as ConfigurationTypes,
      );
      if (extraConfigurationId) configurationId = +extraConfigurationId;
    }

    const { optionsPreselection, missingOptionValues } = getSubConfigurationSelectAndMissingOptionValues(
      subConfigurationOptionValues,
      optionValues,
      options,
      +baseConfigurationId,
      defaultValues.configurationTypes[+baseConfigurationId].defaultOptionValues,
    );

    if (stylePresetId) {
      const matchingPreset = getSubConfigurationPresetPreselectionById(
        stylePresets,
        stylePresetId,
        baseConfigurationId,
      );

      if (matchingPreset) {
        stylePresetsToPreselect = [
          ...stylePresetsToPreselect,
          {
            configurationType: configurationId.toString() as ConfigurationTypes,
            baseStyle: matchingPreset,
          },
        ];
      }
    }

    // Do not preselect size passport if it is a save for later configuration or if it is extra configuration (not base configuration)
    if (sizePassportId && origin !== ConfiguratorOrigin.SaveForLater && baseConfigurationId === configurationId) {
      sizePassportsToPreselect[configurationId] = sizePassportId;
    }

    if (monograms.length > 0) {
      const { missingMonogramValues, monogramsPreselection } = getSubConfigurationSelectAndMissingMonogramValues(
        monograms,
        options,
        baseConfigurationId,
      );

      monogramsToPreselect = { ...monogramsToPreselect, ...monogramsPreselection };

      missingMonogramValuesPerConfig[configurationId] = missingMonogramValues;
    }

    if (!isObjectEmpty(missingOptionValues)) {
      missingOptionValuesPerConfig[configurationId] = missingOptionValues;
    }

    optionsToPreselect = { ...optionsToPreselect, [configurationId]: { ...optionsPreselection } };
  });

  return {
    optionsToPreselect,
    monogramsToPreselect,
    stylePresetsToPreselect,
    sizePassportsToPreselect,
    missingOptionValuesPerConfig,
    missingMonogramValuesPerConfig,
  };
};

const isOptionValueListedInOption = (optionValue: OptionValue, options: Option[]) => {
  const affectedOption = getOptionById(optionValue.optionId, options);
  if (!affectedOption) return false;

  return affectedOption.optionValues?.includes(optionValue.id);
};

const getSubConfigurationSelectAndMissingOptionValues = (
  subConfigurationOptionValues: OptionValue[],
  allOptionValues: typeOptionValue,
  options: OptionConfigurationType,
  configurationTypeId: number,
  defaultOptionValues: { [key: string]: number },
) => {
  let missingOptionValues: MissingOptionValues = {};
  let optionsPreselection: Record<number, number> = {};

  subConfigurationOptionValues.forEach(subConfigurationOptionValue => {
    if (
      allOptionValues[subConfigurationOptionValue.id] &&
      isOptionValueListedInOption(subConfigurationOptionValue, options[configurationTypeId].items)
    ) {
      optionsPreselection[subConfigurationOptionValue.optionId] = subConfigurationOptionValue.id;
    } else {
      // Avoid showing hidden options as missing
      const matchingOption = subConfigurationOptionWithGivenIdExists(
        options,
        configurationTypeId,
        subConfigurationOptionValue.optionId,
      );
      if (matchingOption) {
        missingOptionValues[subConfigurationOptionValue.optionId] = subConfigurationOptionValue;
      }
    }
  });

  options[configurationTypeId].items.forEach(item => {
    if (item.type === 'OPTION_GROUP') {
      item.options?.forEach(option => {
        if (!optionsPreselection[option.id] && !missingOptionValues[option.id]) {
          optionsPreselection[option.id] = defaultOptionValues[option.id];
        }
      });
    } else if (item.type === 'OPTION') {
      if (!optionsPreselection[item.id] && !missingOptionValues[item.id]) {
        optionsPreselection[item.id] = defaultOptionValues[item.id];
      }
    }
  });

  return { optionsPreselection, missingOptionValues };
};

const getSubConfigurationPresetPreselectionById = (
  stylePresets: StylePresets,
  stylePresetId: number,
  subconfigurationTypeId: number,
) => {
  return stylePresets.configurationTypes[subconfigurationTypeId]?.styles.find(style => style.id === stylePresetId);
};

const getSubConfigurationSelectAndMissingMonogramValues = (
  monograms: ConfigurationMonogramDetails[],
  options: OptionConfigurationType,
  subconfigurationTypeId: number,
) => {
  let missingMonogramValues: Monogram = {};
  let monogramsPreselection: Monogram = {};
  let emptyPositionId = -1;

  monograms.forEach(monogram => {
    const subConfigurationMonogramDefinition = options[subconfigurationTypeId].items.find(
      item => item.type === 'MONOGRAM',
    );

    if (!subConfigurationMonogramDefinition) {
      return;
    }

    const { colorId, colorName, fontId, fontName, initialsText, positionId, positionName } = monogram;
    const { colors, positions, fontTypes } = subConfigurationMonogramDefinition;
    let monogramSelection: Monogram = {};
    let missingMonogram: Monogram = {};

    if (positions && positions[positionId]) {
      monogramSelection[positionId] = {
        ...monogramSelection[positionId],
        initials: initialsText,
      };

      if (colors && colors[colorId]) {
        monogramSelection[positionId] = {
          ...monogramSelection[positionId],
          colorId,
        };
      } else {
        missingMonogram[positionId] = {
          ...missingMonogram[positionId],
          colorId,
          colorName,
          positionName: positionName,
        };
      }
      if (fontTypes && fontTypes[fontId]) {
        monogramSelection[positionId] = {
          ...monogramSelection[positionId],
          fontStyleId: fontId,
        };
      } else {
        missingMonogram[positionId] = {
          ...missingMonogram[positionId],
          fontStyleId: fontId,
          fontName,
          positionName: positionName,
        };
      }

      monogramsPreselection = { ...monogramsPreselection, ...monogramSelection };
    } else if (positionId) {
      missingMonogram[positionId] = { positionName: positionName };
    } else {
      missingMonogram[emptyPositionId] = { positionName: positionName };
      emptyPositionId--;
    }

    if (!isObjectEmpty(missingMonogram)) {
      missingMonogramValues = { ...missingMonogramValues, ...missingMonogram };
    }
  });

  return { missingMonogramValues, monogramsPreselection };
};

const getNotAddedOptionalConfigurationsDefaultValues = (
  savedConfiguration: ExistingOrderDetails,
  defaultValues: DefaultValues,
  product: ProductType,
) => {
  // Should not be empty object
  let optionalConfigurationsDefaultValues: OptionsSelection = initialiseConfigurationBasedOptionsSelection(product);
  const configurationTypesInProduct = Products[product];

  const missingOptionalProductInSavedConfig = configurationTypesInProduct.filter(configTypeId => {
    const matchingSubConfig = savedConfiguration.subConfigurations.find(subConfiguration => {
      const isExtraConfiguration = isExtraConfigurationInProduct(product, configTypeId);
      if (isExtraConfiguration) {
        return (
          subConfiguration.configurationTypeId ===
          getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId(+configTypeId) // To handle extra configurations which have different configuration type id but using definitions from base product
        );
      }

      return subConfiguration.configurationTypeId === +configTypeId;
    });

    if (!matchingSubConfig) {
      return +configTypeId;
    }
  });

  if (missingOptionalProductInSavedConfig?.length > 0) {
    missingOptionalProductInSavedConfig.forEach(missingOptionalProductId => {
      const configurationTypeId = +missingOptionalProductId;
      // To handle extra configurations which have different configuration type id but using definitions from base product
      const matchingBaseConfigurationTypeId =
        getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId(+missingOptionalProductId);

      const defaultValuesOfMissingOptionalProduct = defaultValues.configurationTypes[matchingBaseConfigurationTypeId];

      optionalConfigurationsDefaultValues = {
        ...optionalConfigurationsDefaultValues,
        [configurationTypeId]: { ...defaultValuesOfMissingOptionalProduct.defaultOptionValues },
      };
    });
  }

  return optionalConfigurationsDefaultValues;
};

const subConfigurationOptionWithGivenIdExists = (
  options: OptionConfigurationType,
  configurationTypeId: number,
  optionId: number,
) => {
  return options[configurationTypeId].items.some(item => {
    if (item.type === 'OPTION_GROUP') {
      return item.options?.some(option => option.id === optionId);
    }

    return item.id === optionId;
  });
};

export const getOptionById = (optionId: number, options: Option[]): Option | null => {
  for (const option of options) {
    if (option.type === 'OPTION' && option.id === optionId) {
      return option;
    }

    if (option.type === 'OPTION_GROUP') {
      let matchingOptionInAGroup = option.options!.find(option => option.id === optionId);

      if (!matchingOptionInAGroup) {
        continue;
      }

      const tmpMatchingOption = structuredClone(matchingOptionInAGroup);

      if (option.name !== matchingOptionInAGroup.name) {
        // Combine option group and option names
        tmpMatchingOption.name = `${option.name} ${matchingOptionInAGroup.name} `;
      }

      return tmpMatchingOption;
    }
  }

  return null;
};

export const getOptionAndMatchingOptionGroupByOptionId = (
  optionId: number,
  options: Option[],
): { option: Option; optionGroup: Option | null } | null => {
  for (const option of options) {
    if (option.type === 'OPTION_GROUP') {
      let matchingOption = option.options!.find(option => option.id === optionId);

      if (matchingOption) {
        const tmpMatchingOption = { ...matchingOption };
        // Combine option group and option names
        tmpMatchingOption.name = `${option.name} ${matchingOption.name} `;
        return { option: tmpMatchingOption, optionGroup: option };
      }
    }

    if (option.id === optionId) {
      return { option, optionGroup: null };
    }
  }

  return null;
};

export const areMissingValuesEmpty = (
  missingValues: MissingMonogramValuesPerConfig | MissingOptionValuesPerConfiguration,
) => {
  if (!missingValues || isObjectEmpty(missingValues)) {
    return true;
  }

  return Object.values(missingValues).every(missingMonogramValue => isObjectEmpty(missingMonogramValue));
};

export const getFirstSectionWithMissingValues = (
  missingOptionSelection:
    | MissingOptionValuesPerConfiguration
    | MissingMonogramValuesPerConfig
    | MissingOptionsPerConfiguration,
) => {
  const firstAffectedConfigurationId = Object.keys(missingOptionSelection).sort((a, b) => {
    return +b - +a;
  })[0];
  const productMatchingSection = getSectionByConfigurationType(firstAffectedConfigurationId);

  return productMatchingSection;
};

export const getSelectedIdsWithMissingValues = (
  missingValues: MissingOptionValuesPerConfiguration | MissingOptionsPerConfiguration | MissingMonogramValuesPerConfig,
) => {
  let allMissingOptionIds: number[] = [];

  Object.values(missingValues).forEach(missingValue => {
    if (!isObjectEmpty(missingValue)) {
      const keys = Object.keys(missingValue).map(Number);
      allMissingOptionIds = [...allMissingOptionIds, ...keys];
    }
  });

  return allMissingOptionIds;
};

export const getSelectedOptionsWithMissingValues = (
  missingValues: MissingOptionValuesPerConfiguration,
  options: OptionConfigurationType,
) => {
  let missingOptionsPerConfiguration: MissingOptionsPerConfiguration = {};
  Object.entries(missingValues).forEach(([configurationId, missingOptionValues]) => {
    const baseConfigurationId = getMatchingConfigurationTypeIdForGivenExtraConfigurationTypeId(+configurationId);

    if (!isObjectEmpty(missingOptionValues)) {
      const optionIds = Object.keys(missingOptionValues).map(Number);
      optionIds.forEach(optionId => {
        const option = getOptionById(optionId, options[baseConfigurationId].items);
        if (option) {
          missingOptionsPerConfiguration[+configurationId] = {
            ...missingOptionsPerConfiguration[+configurationId],
            [optionId]: option,
          };
        }
      });
    }
  });
  return missingOptionsPerConfiguration;
};

const getAdditionalProductSavedConfigurations = (savedConfiguration: ExistingOrderDetails | undefined) => {
  let isAdditionalProductAdded: Record<string, boolean> = {};

  if (savedConfiguration && savedConfiguration?.subConfigurations.length > 1) {
    savedConfiguration?.subConfigurations.forEach(configuration => {
      if (configuration.isExtraProduct) {
        const extraConfigurationId = getExtraConfgurationByBaseConfiguration(
          savedConfiguration.product,
          configuration.configurationTypeId.toString() as ConfigurationTypes,
        );
        const configurationId = extraConfigurationId ?? configuration.configurationTypeId;
        isAdditionalProductAdded[configurationId] = true;
      }
    });
  }

  return isAdditionalProductAdded;
};

export const getPreloadConfigurationDefaultSelections = (
  savedConfiguration: ExistingOrderDetails | undefined,
  defaultSection: Section,
  product: ProductType,
  fabrics: typeFabricList,
  options: OptionConfigurationType,
  optionValues: typeOptionValue,
  defaultValues: DefaultValues,
  stylePresets: StylePresets,
  origin: ConfiguratorOriginsType,
) => {
  let sectionToStart: Section = defaultSection;
  let fabricPreselection: Fabric | undefined;
  let optionsPreselection: OptionsSelection = initialiseConfigurationBasedOptionsSelection(product);
  let monogramsPreselection: Monogram = {};
  let stylePresetsPreselection: Record<string, StylePreset> = {};
  let sizePassportPreselection: SizePassportSelection = {};
  let missingOptionValues: MissingOptionValuesPerConfiguration = {};
  let missingMonogramValues: MissingMonogramValuesPerConfig = {};
  let missingOptionsPerConfig: MissingOptionsPerConfiguration = {};
  let monogramsIdsWithMissingSelection: number[] = [];
  let showMissingFabricModal: boolean = false;
  let showMissingOptionsPanel: boolean = false;
  let isAdditionalProductAdded: Record<string, boolean> = getAdditionalProductSavedConfigurations(savedConfiguration);

  if (savedConfiguration && !isObjectEmpty(savedConfiguration)) {
    fabricPreselection = getExistingConfigurationSelectedFabric(savedConfiguration, fabrics);

    const {
      optionsToPreselect,
      monogramsToPreselect,
      stylePresetsToPreselect,
      sizePassportsToPreselect,
      missingOptionValuesPerConfig,
      missingMonogramValuesPerConfig,
    } = getExistingConfigurationSelectAndMissingOptionValues(
      savedConfiguration,
      options,
      optionValues,
      defaultValues,
      product,
      stylePresets,
      origin,
    );

    const areOptionMissingValuesEmpty = areMissingValuesEmpty(missingOptionValuesPerConfig);
    const areMonogramMissingValuesEmpty = areMissingValuesEmpty(missingMonogramValuesPerConfig);

    optionsPreselection = optionsToPreselect;
    monogramsPreselection = monogramsToPreselect;
    sizePassportPreselection = sizePassportsToPreselect;
    missingOptionValues = missingOptionValuesPerConfig;
    missingMonogramValues = areMonogramMissingValuesEmpty ? {} : missingMonogramValuesPerConfig;
    missingOptionsPerConfig = getSelectedOptionsWithMissingValues(missingOptionValuesPerConfig, options);
    monogramsIdsWithMissingSelection = getSelectedIdsWithMissingValues(missingMonogramValuesPerConfig);

    stylePresetsToPreselect.forEach(preset => {
      stylePresetsPreselection[preset.configurationType] = preset.baseStyle;
    });

    if (fabricPreselection && areOptionMissingValuesEmpty && areMonogramMissingValuesEmpty) {
      sectionToStart = Section.Summary;
    } else if (!fabricPreselection) {
      sectionToStart = Section.Fabric;
      showMissingFabricModal = true;
    } else {
      sectionToStart = isObjectEmpty(missingOptionValuesPerConfig)
        ? getFirstSectionWithMissingValues(missingMonogramValuesPerConfig)
        : getFirstSectionWithMissingValues(missingOptionValuesPerConfig);
      showMissingOptionsPanel = true;
    }
  }

  return {
    fabricPreselection,
    optionsPreselection,
    monogramsPreselection,
    stylePresetsPreselection,
    sizePassportPreselection,
    missingOptionValues,
    missingMonogramValues,
    sectionToStart,
    missingOptionsPerConfig,
    monogramsIdsWithMissingSelection,
    showMissingFabricModal,
    showMissingOptionsPanel,
    isAdditionalProductAdded,
  };
};
